/**
 * «Próximos vencimientos» — the pure half of the client: types, query keys and
 * the envelope → page mapping. No `api.ts` import (it reads `import.meta.env`),
 * so the prerender script can use the SAME keys and mapper the page uses — a
 * seed under a different key or shape is a silent cache miss and the page
 * prerenders as an empty shell (see the /directory note in entry-server.tsx).
 */

export const EXPIRING_PATH = '/vencimientos';
export const EXPIRING_WINDOWS = ['3m', '6m', '12m'] as const;
export type ExpiringWindow = (typeof EXPIRING_WINDOWS)[number];
/** The API's default window, and the page's. */
export const DEFAULT_EXPIRING_WINDOW: ExpiringWindow = '6m';
export const EXPIRING_PAGE_SIZE = 20;

export interface ExpiringFilters {
  window: ExpiringWindow;
  /** Eight-digit CPV code (a division `90000000` from the select). */
  cpv?: string;
  /** NUTS-2 comunidad (`ES30`). */
  region?: string;
  /** The buyer's NIF. */
  buyer?: string;
  q?: string;
  /** 1-based. */
  page: number;
}

export interface ExpiringItem {
  id: string;
  subject: string | null;
  cpv: { code: string; name: string | null } | null;
  region: { code: string; name: string | null } | null;
  buyer: { tin: string | null; name: string | null };
  /** Present only for a viewer who may see the award (Pro, or no wall). */
  winnerName?: string | null;
  winnerTin?: string | null;
  amount?: string | null;
  outcomeLocked: boolean;
  awardedAt: string | null;
  startAt: string | null;
  endAt: string;
  endAtSource: 'duration' | 'explicit' | null;
  extension: string | null;
  method: string | null;
  tender: { id: string; slug: string | null } | null;
}

export interface ExpiringPageResult {
  items: ExpiringItem[];
  total: number;
  offset: number;
  limit: number;
  viewerIsPro: boolean;
}

export type ExpiringCounts = Record<ExpiringWindow, number>;

export interface ExpiringDivision {
  code: string;
  name: string | null;
  count: number;
}

/** `GET /results/expiring` as the envelope interceptor ships it. */
export interface ExpiringEnvelope {
  data?: ExpiringItem[];
  meta?: {
    pagination?: { total?: number; offset?: number; limit?: number };
    viewerIsPro?: boolean;
  };
}

export function expiringPageFromEnvelope(env: ExpiringEnvelope): ExpiringPageResult {
  return {
    items: env.data ?? [],
    total: env.meta?.pagination?.total ?? 0,
    offset: env.meta?.pagination?.offset ?? 0,
    limit: env.meta?.pagination?.limit ?? EXPIRING_PAGE_SIZE,
    viewerIsPro: env.meta?.viewerIsPro ?? false,
  };
}

/** The filter axes the counts / divisions depend on (never the window or page). */
function axes(f: Pick<ExpiringFilters, 'cpv' | 'region' | 'buyer' | 'q'>) {
  return {
    cpv: f.cpv || undefined,
    region: f.region || undefined,
    buyer: f.buyer || undefined,
    q: f.q || undefined,
  };
}

export function expiringListKey(f: ExpiringFilters): readonly unknown[] {
  return ['expiring', 'list', { window: f.window, page: f.page, ...axes(f) }];
}

/** Shared by the page's window chips and the sidebar row (no filters there). */
export function expiringCountsKey(
  f: Pick<ExpiringFilters, 'cpv' | 'region' | 'buyer' | 'q'> = {},
): readonly unknown[] {
  return ['expiring', 'counts', axes(f)];
}

export function expiringDivisionsKey(f: ExpiringFilters): readonly unknown[] {
  return ['expiring', 'divisions', { window: f.window, ...axes({ ...f, cpv: undefined }) }];
}

/** Query string for the API (and nothing the API does not read). */
export function expiringQueryString(
  f: Partial<ExpiringFilters>,
  extra: Record<string, string | number> = {},
): string {
  const qs = new URLSearchParams();
  if (f.window) qs.set('window', f.window);
  if (f.cpv) qs.set('cpv', f.cpv);
  if (f.region) qs.set('region', f.region);
  if (f.buyer) qs.set('buyer', f.buyer);
  if (f.q) qs.set('q', f.q);
  for (const [k, v] of Object.entries(extra)) qs.set(k, String(v));
  return qs.toString();
}

/** The page's own URL params → filters (garbage → defaults, as the API does). */
export function expiringFiltersFromSearch(params: URLSearchParams): ExpiringFilters {
  const w = params.get('plazo') ?? '';
  const page = Number.parseInt(params.get('page') ?? '1', 10);
  return {
    window: (EXPIRING_WINDOWS as readonly string[]).includes(w)
      ? (w as ExpiringWindow)
      : DEFAULT_EXPIRING_WINDOW,
    cpv: /^\d{8}$/.test(params.get('cpv') ?? '') ? (params.get('cpv') as string) : undefined,
    region: params.get('comunidad') || undefined,
    buyer: params.get('organo') || undefined,
    q: params.get('q') || undefined,
    page: Number.isFinite(page) && page > 1 ? page : 1,
  };
}

/** The default view — what the prerender seeds and the sidebar row counts. */
export const DEFAULT_EXPIRING_FILTERS: ExpiringFilters = {
  window: DEFAULT_EXPIRING_WINDOW,
  page: 1,
};
